import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
    Users,
    Loader2,
    UserPlus,
    Copy,
    Check,
    X,
    ShieldCheck,
    UserCog,
} from "lucide-react";
import { toast } from "sonner";
import { api, useAuth } from "@/lib/auth";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const ROLE_PILL = {
    master_admin: "bg-amber-100 text-amber-900 border-amber-300",
    admin: "bg-mm-navy/10 text-mm-navy border-mm-navy/30",
    agent: "bg-sky-50 text-sky-800 border-sky-200",
    user: "bg-slate-100 text-slate-700 border-slate-200",
};

const STATUS_PILL = {
    active: "bg-emerald-50 text-emerald-800 border-emerald-200",
    pending: "bg-amber-50 text-amber-900 border-amber-200",
    inactive: "bg-rose-50 text-rose-800 border-rose-200",
};

export default function AdminTeam() {
    const { user } = useAuth();
    const isMaster = user?.role === "master_admin";
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filter, setFilter] = useState({ q: "", role: "", status: "" });
    const [inviteOpen, setInviteOpen] = useState(false);
    const [revealed, setRevealed] = useState(null);

    const load = async () => {
        setLoading(true);
        try {
            const params = {};
            if (filter.q) params.q = filter.q;
            if (filter.role) params.role = filter.role;
            if (filter.status) params.status = filter.status;
            const r = await api.get("/admin/team", { params });
            setItems(r.data?.items || []);
        } catch {
            toast.error("Couldn't load team.");
        } finally {
            setLoading(false);
        }
    };
    useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter.role, filter.status]);

    const onInvited = ({ member, temp_password, expires_at }) => {
        setItems((prev) => [member, ...prev]);
        setRevealed({ member, temp_password, expires_at });
        toast.success(`Invite created for ${member.email}.`);
    };

    return (
        <div className="space-y-5" data-testid="admin-team">
            <header className="flex flex-wrap items-end justify-between gap-3">
                <div>
                    <div className="mm-eyebrow">Operations</div>
                    <h1 className="mm-display text-3xl text-mm-navy">Team</h1>
                    <p className="mt-1 text-sm text-mm-muted">
                        Invite agents, reset passwords, manage access. Master admin is locked.
                    </p>
                </div>
                <div className="flex items-center gap-2">
                    <Input
                        value={filter.q}
                        onChange={(e) => setFilter((f) => ({ ...f, q: e.target.value }))}
                        onKeyDown={(e) => { if (e.key === "Enter") load(); }}
                        placeholder="Search name / email"
                        className="w-56"
                        data-testid="team-search"
                    />
                    <select
                        value={filter.role}
                        onChange={(e) => setFilter((f) => ({ ...f, role: e.target.value }))}
                        className="rounded-md border border-slate-200 bg-white px-3 py-2 text-sm"
                        data-testid="team-role-filter"
                    >
                        <option value="">All roles</option>
                        <option value="master_admin">Master admin</option>
                        <option value="admin">Admin</option>
                        <option value="agent">Agent</option>
                    </select>
                    <select
                        value={filter.status}
                        onChange={(e) => setFilter((f) => ({ ...f, status: e.target.value }))}
                        className="rounded-md border border-slate-200 bg-white px-3 py-2 text-sm"
                        data-testid="team-status-filter"
                    >
                        <option value="">All statuses</option>
                        <option value="active">Active</option>
                        <option value="pending">Pending</option>
                        <option value="inactive">Inactive</option>
                    </select>
                    <button
                        onClick={() => setInviteOpen(true)}
                        className="mm-btn-primary !py-2 text-sm"
                        data-testid="team-invite-button"
                    >
                        <UserPlus size={14} />
                        Invite
                    </button>
                </div>
            </header>

            <div className="mm-card overflow-hidden">
                {loading ? (
                    <div className="py-10 text-center"><Loader2 className="mx-auto animate-spin text-mm-navy" /></div>
                ) : items.length === 0 ? (
                    <div className="py-10 text-center text-mm-muted text-sm">No team members yet.</div>
                ) : (
                    <table className="w-full text-sm">
                        <thead className="bg-mm-bg text-[11px] uppercase tracking-wider text-mm-muted">
                            <tr>
                                <Th>Name</Th>
                                <Th>Email</Th>
                                <Th>Role</Th>
                                <Th>Status</Th>
                                <Th className="text-right">Leads</Th>
                                <Th className="text-right">Calls</Th>
                                <Th>First Touch SLA</Th>
                                <Th className="text-right">Deals</Th>
                                <Th>Last active</Th>
                            </tr>
                        </thead>
                        <tbody>
                            {items.map((m) => (
                                <tr
                                    key={m.id}
                                    className="cursor-pointer border-t border-mm-line align-top hover:bg-mm-bg"
                                    data-testid={`team-row-${m.id}`}
                                >
                                    <Td>
                                        <Link to={`/admin/team/${m.id}`} className="block font-medium text-mm-navy hover:text-mm-blue">
                                            {[m.first_name, m.last_name].filter(Boolean).join(" ") || m.email}
                                        </Link>
                                        {m.username && (
                                            <span className="mt-1 inline-flex rounded-full bg-mm-bg px-2 py-0.5 font-mono text-[10px] text-mm-muted">
                                                @{m.username}
                                            </span>
                                        )}
                                    </Td>
                                    <Td className="text-mm-ink">{m.email}</Td>
                                    <Td><RolePill role={m.role} /></Td>
                                    <Td><StatusPill status={m.status} /></Td>
                                    <Td className="text-right tabular-nums text-mm-ink" data-testid={`team-leads-count-${m.id}`}>
                                        {m.role === "agent" ? (m.leads_assigned ?? 0) : "—"}
                                    </Td>
                                    <Td className="text-right text-mm-muted">—</Td>
                                    <Td data-testid={`team-sla-${m.id}`}>
                                        <SlaCell member={m} />
                                    </Td>
                                    <Td className="text-right text-mm-muted">—</Td>
                                    <Td className="text-mm-muted">
                                        {m.last_login_at ? new Date(m.last_login_at).toLocaleString() : "—"}
                                    </Td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>

            <InviteModal
                open={inviteOpen}
                onOpenChange={setInviteOpen}
                isMaster={isMaster}
                onInvited={onInvited}
            />
            {revealed && (
                <TempPasswordReveal
                    payload={revealed}
                    onClose={() => setRevealed(null)}
                />
            )}
        </div>
    );
}

function InviteModal({ open, onOpenChange, isMaster, onInvited }) {
    const [form, setForm] = useState({
        first_name: "", last_name: "", email: "",
        phone: "", license_number: "", states: "", start_date: "", role: "agent",
    });
    const [loading, setLoading] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        if (!form.first_name || !form.last_name || !form.email) return;
        setLoading(true);
        try {
            const states = form.states
                ? form.states.split(",").map((s) => s.trim().toUpperCase()).filter(Boolean)
                : [];
            const r = await api.post("/admin/team/invite", {
                first_name: form.first_name.trim(),
                last_name: form.last_name.trim(),
                email: form.email.trim(),
                phone: form.phone || null,
                license_number: form.license_number || null,
                states_licensed: states,
                start_date: form.start_date || null,
                role: form.role,
            });
            onInvited(r.data);
            onOpenChange(false);
            setForm({ first_name: "", last_name: "", email: "", phone: "", license_number: "", states: "", start_date: "", role: "agent" });
        } catch (err) {
            const msg = err?.response?.data?.detail || "Couldn't invite.";
            toast.error(typeof msg === "string" ? msg : "Couldn't invite.");
        } finally { setLoading(false); }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="sm:max-w-lg" data-testid="team-invite-modal">
                <DialogHeader>
                    <DialogTitle className="mm-display text-2xl">Invite a team member</DialogTitle>
                </DialogHeader>
                <form onSubmit={submit} className="mt-2 space-y-4">
                    <div className="grid grid-cols-2 gap-3">
                        <FieldI label="First name" value={form.first_name} onChange={(v) => setForm((s) => ({ ...s, first_name: v }))} testid="invite-first" required />
                        <FieldI label="Last name"  value={form.last_name}  onChange={(v) => setForm((s) => ({ ...s, last_name: v }))}  testid="invite-last"  required />
                    </div>
                    <FieldI label="Email" type="email" value={form.email} onChange={(v) => setForm((s) => ({ ...s, email: v }))} testid="invite-email" required />
                    <div className="grid grid-cols-2 gap-3">
                        <FieldI label="Phone" value={form.phone} onChange={(v) => setForm((s) => ({ ...s, phone: v }))} testid="invite-phone" />
                        <FieldI label="Start date" type="date" value={form.start_date} onChange={(v) => setForm((s) => ({ ...s, start_date: v }))} testid="invite-start" />
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                        <FieldI label="License #" value={form.license_number} onChange={(v) => setForm((s) => ({ ...s, license_number: v }))} testid="invite-license" />
                        <FieldI label="States (comma-sep)" value={form.states} onChange={(v) => setForm((s) => ({ ...s, states: v }))} placeholder="FL, TX" testid="invite-states" />
                    </div>
                    <div>
                        <Label className="mb-1.5 block text-xs font-medium uppercase tracking-wider text-mm-muted">Role</Label>
                        <select
                            value={form.role}
                            onChange={(e) => setForm((s) => ({ ...s, role: e.target.value }))}
                            className="w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-sm"
                            data-testid="invite-role"
                        >
                            <option value="agent">Agent</option>
                            {isMaster && <option value="admin">Admin (master only)</option>}
                        </select>
                    </div>
                    <button
                        type="submit"
                        disabled={loading || !form.first_name || !form.last_name || !form.email}
                        className="mm-btn-primary w-full !py-3 disabled:opacity-60"
                        data-testid="invite-submit"
                    >
                        {loading ? <><Loader2 size={14} className="animate-spin" /> Sending invite…</> : <><UserPlus size={14} /> Send invite</>}
                    </button>
                </form>
            </DialogContent>
        </Dialog>
    );
}

function FieldI({ label, value, onChange, type = "text", required, placeholder, testid }) {
    return (
        <div>
            <Label className="mb-1.5 block text-xs font-medium uppercase tracking-wider text-mm-muted">
                {label}{required && <span className="text-rose-600"> *</span>}
            </Label>
            <Input type={type} value={value} onChange={(e) => onChange(e.target.value)} required={required} placeholder={placeholder} data-testid={testid} />
        </div>
    );
}

function TempPasswordReveal({ payload, onClose }) {
    const { temp_password, expires_at, member } = payload;
    const [copied, setCopied] = useState(false);
    const [secondsLeft, setSecondsLeft] = useState(60);

    useEffect(() => {
        const t = setInterval(() => {
            setSecondsLeft((s) => {
                if (s <= 1) { clearInterval(t); onClose(); return 0; }
                return s - 1;
            });
        }, 1000);
        return () => clearInterval(t);
    }, [onClose]);

    const copy = async () => {
        try {
            await navigator.clipboard.writeText(temp_password);
            setCopied(true);
            toast.success("Temp password copied to clipboard.");
            setTimeout(() => setCopied(false), 3000);
        } catch {
            toast.error("Copy failed — please select and copy manually.");
        }
    };

    return (
        <div
            className="fixed inset-x-0 bottom-6 z-50 mx-auto max-w-md px-4"
            data-testid="team-temp-password-panel"
        >
            <div className="mm-card border-amber-300 bg-amber-50 p-4 shadow-xl">
                <div className="flex items-start justify-between gap-3">
                    <div className="flex-1">
                        <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-amber-900">
                            <ShieldCheck size={14} />
                            Temp password — copy now
                        </div>
                        <p className="mt-1 text-xs text-amber-900/80">
                            Email delivery is in sandbox mode. Share this with{" "}
                            <span className="font-mono">{member.email}</span> if they don't see the welcome email.
                        </p>
                        <div className="mt-3 flex items-center gap-2">
                            <code
                                className="inline-block flex-1 rounded-lg border border-amber-300 bg-white px-3 py-2 font-mono text-sm text-mm-navy"
                                data-testid="team-temp-password-code"
                            >
                                {temp_password}
                            </code>
                            <button
                                onClick={copy}
                                className="mm-btn-outline !py-2 text-xs"
                                data-testid="team-temp-password-copy"
                            >
                                {copied ? <><Check size={12} /> Copied</> : <><Copy size={12} /> Copy</>}
                            </button>
                        </div>
                        <p className="mt-2 text-[11px] text-amber-900/70">
                            Expires {new Date(expires_at).toLocaleString()} · auto-dismisses in {secondsLeft}s
                        </p>
                    </div>
                    <button
                        onClick={onClose}
                        className="rounded-full p-1 text-amber-900/70 hover:bg-amber-100"
                        aria-label="Dismiss"
                        data-testid="team-temp-password-dismiss"
                    >
                        <X size={14} />
                    </button>
                </div>
            </div>
        </div>
    );
}

function RolePill({ role }) {
    const cls = ROLE_PILL[role] || ROLE_PILL.user;
    const label = role === "master_admin" ? "Master Admin"
        : role === "admin" ? "Admin"
            : role === "agent" ? "Agent" : "User";
    return (
        <span className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-medium ${cls}`} data-testid={`role-pill-${role}`}>
            {role === "master_admin" && <ShieldCheck size={10} />}
            {role === "admin" && <UserCog size={10} />}
            {role === "agent" && <Users size={10} />}
            {label}
        </span>
    );
}

function StatusPill({ status }) {
    const cls = STATUS_PILL[status] || STATUS_PILL.pending;
    const label = (status || "pending").charAt(0).toUpperCase() + (status || "pending").slice(1);
    return (
        <span className={`inline-flex rounded-full border px-2 py-0.5 text-[10px] font-medium ${cls}`} data-testid={`status-pill-${status}`}>
            {label}
        </span>
    );
}

function Th({ children, className = "" }) {
    return <th className={`px-3 py-2.5 text-left font-medium ${className}`}>{children}</th>;
}
function Td({ children, className = "" }) {
    return <td className={`px-3 py-2.5 align-top ${className}`}>{children}</td>;
}

function SlaCell({ member }) {
    if (member.role !== "agent") {
        return <span className="text-xs text-mm-muted">—</span>;
    }
    const avg = member.sla_avg_minutes;
    const sample = member.sla_sample_size || 0;
    if (avg == null || sample === 0) {
        return (
            <div className="flex flex-col gap-0.5">
                <span className="inline-flex w-fit items-center gap-1 rounded-full border border-mm-line bg-mm-bg/60 px-2 py-0.5 text-[10px] font-medium text-mm-muted">
                    — no data
                </span>
                <span className="text-[10px] text-mm-muted">0 leads contacted</span>
            </div>
        );
    }
    const tone =
        avg <= 5 ? "green" : avg <= 15 ? "amber" : "red";
    const pill = {
        green: "border-emerald-200 bg-emerald-50 text-emerald-800",
        amber: "border-amber-200 bg-amber-50 text-amber-900",
        red: "border-rose-200 bg-rose-50 text-rose-800",
    }[tone];
    const dot = {
        green: "bg-emerald-500",
        amber: "bg-amber-500",
        red: "bg-rose-500",
    }[tone];
    const display =
        avg < 1 ? "<1m" : avg < 60 ? `${Math.round(avg)}m` : `${(avg / 60).toFixed(1)}h`;
    return (
        <div className="flex flex-col gap-0.5">
            <span className={`inline-flex w-fit items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-medium ${pill}`}>
                <span className={`h-1.5 w-1.5 rounded-full ${dot}`} />
                {display}
            </span>
            <span className="text-[10px] text-mm-muted">{sample} leads contacted</span>
        </div>
    );
}
